import { createAsyncThunk } from '@reduxjs/toolkit';
import { auth } from '@utils/auth';
import { getODataConjunctionFilterFrom, getODataSearchFilterFrom } from '@utils/odata';
import { interpolateString } from '@utils/string';
import {
  createWorkspaceApi,
  deleteModelRegistryTokenApi,
  deleteUserApi,
  deleteWorkspaceApi,
  getModelRegistryTokensApi,
  getOrganizationPermissionsApi,
  getOrganizationsApi,
  getWorkspacesApi,
  inviteUserApi,
  postModelRegistryTokenApi,
  updateUserRoleApi,
} from '@api/organization';
import { getAllUsersApi } from '@api/users';
import { PEOPLE_BLOCK } from '@constants/common';
import { UserRoles } from '@constants/enum/common';
import {
  ADD_TOKEN_NOTIFICATION_SUCCES,
  ADD_WORKSPACE_NOTIFICATION_SUCCESS,
  CHANGE_USER_ROLE_NOTIFICATION_SUCCESS,
  DELETE_TOKEN_NOTIFICATION_SUCCESS,
  DELETE_WORKSPACE_NOTIFICATION_SUCCESS,
  INVITE_USER_NOTIFICATION_SUCCESS,
  REMOVE_USER_NOTIFICATION_SUCCESS,
} from '@constants/organization';
import { MODEL_PROVIDERS_NAMES_MAPPED } from '@constants/settings-page';
import { DEFAULT_WORKSPACE } from '@constants/workspace';
import { persistor, RootState } from '@redux/store';
import {
  ADD_WORKSPACE,
  CHANGE_ORGANIZATION,
  CHANGE_USER_ROLE,
  DELETE_MODEL_REGISTRY_TOKEN,
  DELETE_WORKSPACE,
  FETCH_MODEL_REGISTRY_TOKEN,
  FETCH_NON_WORKSPACE_USERS,
  FETCH_ORGANIZATION_PERMISSIONS,
  FETCH_ORGANIZATIONS,
  FETCH_USERS,
  FETCH_WORKSPACE_USERS,
  FETCH_WORKSPACES,
  IInviteUserData,
  INVITE_USER,
  IOrganizationData,
  ModelProviders,
  POST_MODEL_REGISTRY_TOKEN,
  REMOVE_USER,
  RESET_ORGANIZATION_MESSAGE,
  SET_CURRENT_WORKSPACE,
  SET_ORGANIZATION_INFO,
} from '@redux/types/types';
import { addErrorNotification, addSuccessNotification } from './notificationActions';

export const resetMessage = {
  type: RESET_ORGANIZATION_MESSAGE,
};

export const organizationChange = (organizationId: string) => {
  // update the current organization id in the auth util
  // So the Requests include the new organization id
  auth.setCurrentOrganizationId(organizationId);
  persistor.purge().then(() => {
    return persistor.flush();
  });

  return {
    type: CHANGE_ORGANIZATION,
    payload: organizationId,
  };
};

export function setOrganizationInfo(organization: IOrganizationData) {
  return {
    type: SET_ORGANIZATION_INFO,
    payload: organization,
  };
}

export const fetchOrganizations = createAsyncThunk(
  FETCH_ORGANIZATIONS,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getOrganizationsApi();
      return data?.organizations ?? [];
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchUsers = createAsyncThunk(
  FETCH_USERS,
  async ({ page, limit }: { page?: number; limit?: number }, { dispatch, rejectWithValue }) => {
    const params = {
      page_number: page ?? 1,
      limit: limit ?? PEOPLE_BLOCK.USERS_PER_PAGE,
    };

    try {
      const { data } = await getAllUsersApi(params);
      return data;
    } catch (error) {
      dispatch(addErrorNotification(error));
      return rejectWithValue(error);
    }
  },
);

export const fetchWorkspaceUsers = createAsyncThunk(
  FETCH_WORKSPACE_USERS,
  async (
    { workspaceName, page, limit }: { page?: number; limit?: number; workspaceName: string },
    { dispatch, rejectWithValue, getState },
  ) => {
    const { organization } = (getState() as RootState).organizationStore;

    const organizationId = organization?.organizationId ?? '';
    const filter = getODataConjunctionFilterFrom(
      `organizations/any(o: o/organization_id eq '${organizationId}' and o/role ne '${UserRoles.ADMIN}')`,
      `workspaces/any(w: w/name eq '${workspaceName}')`,
    );

    const params = {
      page_number: page ?? 1,
      limit: limit ?? PEOPLE_BLOCK.USERS_PER_PAGE,
      filter,
    };

    try {
      const { data } = await getAllUsersApi(params);
      return data;
    } catch (error) {
      dispatch(addErrorNotification(error));
      return rejectWithValue(error);
    }
  },
);

export const fetchNonWorkspaceUsers = createAsyncThunk(
  FETCH_NON_WORKSPACE_USERS,
  async (
    {
      workspaceName,
      page,
      limit,
      searchValue,
    }: {
      page?: number;
      limit?: number;
      workspaceName: string;
      fetchMore?: boolean;
      searchValue?: string;
    },
    { dispatch, rejectWithValue, getState },
  ) => {
    const { organization } = (getState() as RootState).organizationStore;

    const organizationId = organization?.organizationId ?? '';
    const searchFilter = searchValue ? getODataSearchFilterFrom('given_name', searchValue) : null;
    const filter = getODataConjunctionFilterFrom(
      `organizations/any(o: o/organization_id eq '${organizationId}' and o/role ne '${UserRoles.ADMIN}')`,
      `not workspaces/any(w: w/name eq '${workspaceName}')`,
      searchFilter,
    );

    const params = {
      page_number: page ?? 1,
      limit: limit ?? PEOPLE_BLOCK.USERS_PER_PAGE,
      filter,
    };

    try {
      const { data } = await getAllUsersApi(params);
      return data;
    } catch (error) {
      dispatch(addErrorNotification(error));
      return rejectWithValue(error);
    }
  },
);

export const inviteUser = createAsyncThunk(
  INVITE_USER,
  async (
    { user, organizationId }: { user: IInviteUserData; organizationId: string },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const inviteUserResponse = await inviteUserApi(user, organizationId);
      dispatch(addSuccessNotification(INVITE_USER_NOTIFICATION_SUCCESS));
      return inviteUserResponse.data;
    } catch (error) {
      dispatch(addErrorNotification(error));
      return rejectWithValue(error);
    }
  },
);

export const changeUserRole = createAsyncThunk(
  CHANGE_USER_ROLE,
  async ({ userId, role }: { userId: string; role: UserRoles }, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateUserRoleApi(userId, role);
      dispatch(addSuccessNotification(CHANGE_USER_ROLE_NOTIFICATION_SUCCESS));
      return response;
    } catch (error) {
      dispatch(addErrorNotification(error));
      return rejectWithValue(error);
    }
  },
);

export const removeUser = createAsyncThunk(
  REMOVE_USER,
  async (userId: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await deleteUserApi(userId);
      dispatch(addSuccessNotification(REMOVE_USER_NOTIFICATION_SUCCESS));
      return response;
    } catch (error) {
      dispatch(addErrorNotification(error));
      return rejectWithValue(error);
    }
  },
);

export const fetchModelRegistryTokens = createAsyncThunk(
  FETCH_MODEL_REGISTRY_TOKEN,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await getModelRegistryTokensApi();
      return response.data;
    } catch (error) {
      dispatch(addErrorNotification(error));
      return rejectWithValue(error);
    }
  },
);

export const postModelRegistryToken = createAsyncThunk(
  POST_MODEL_REGISTRY_TOKEN,
  async (
    { modelToken, modelProvider }: { modelToken: string; modelProvider: ModelProviders },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await postModelRegistryTokenApi(modelToken, modelProvider);
      dispatch(
        addSuccessNotification(
          interpolateString(
            ADD_TOKEN_NOTIFICATION_SUCCES,
            { modelProvider: MODEL_PROVIDERS_NAMES_MAPPED[modelProvider] },
            false,
          ) as string,
        ),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteModelRegistryToken = createAsyncThunk(
  DELETE_MODEL_REGISTRY_TOKEN,
  async (modelProvider: ModelProviders, { dispatch, rejectWithValue }) => {
    try {
      const response = await deleteModelRegistryTokenApi(modelProvider);
      dispatch(
        addSuccessNotification(
          interpolateString(
            DELETE_TOKEN_NOTIFICATION_SUCCESS,
            { modelProvider: MODEL_PROVIDERS_NAMES_MAPPED[modelProvider] },
            false,
          ) as string,
        ),
      );
      return response.data;
    } catch (error) {
      dispatch(addErrorNotification(error));
      return rejectWithValue(error);
    }
  },
);

export const getWorkspaces = createAsyncThunk(
  FETCH_WORKSPACES,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await getWorkspacesApi();
      return response.data;
    } catch (error) {
      dispatch(addErrorNotification(error));
      return rejectWithValue(error);
    }
  },
);

export const createWorkspace = createAsyncThunk(
  ADD_WORKSPACE,
  async (workspace: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await createWorkspaceApi(workspace);
      dispatch(addSuccessNotification(ADD_WORKSPACE_NOTIFICATION_SUCCESS));
      return response.data;
    } catch (error) {
      dispatch(addErrorNotification(error));
      return rejectWithValue(error);
    }
  },
);

export const setWorkspace = (workpsace: string) => {
  persistor.purge().then(() => {
    return persistor.flush();
  });
  return {
    type: SET_CURRENT_WORKSPACE,
    payload: workpsace,
  };
};

export const deleteWorkspace = createAsyncThunk(
  DELETE_WORKSPACE,
  async (workspace: string, { dispatch, rejectWithValue }) => {
    try {
      await deleteWorkspaceApi(workspace);
      dispatch(getWorkspaces());
      dispatch(setWorkspace(DEFAULT_WORKSPACE));
      dispatch(
        addSuccessNotification(
          interpolateString(DELETE_WORKSPACE_NOTIFICATION_SUCCESS, { workspace }, false) as string,
        ),
      );
      return workspace;
    } catch (error) {
      dispatch(addErrorNotification(error));
      return rejectWithValue(error);
    }
  },
);

export const fetchOrganizationPermissions = createAsyncThunk(
  FETCH_ORGANIZATION_PERMISSIONS,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await getOrganizationPermissionsApi();
      return response.data;
    } catch (error) {
      dispatch(addErrorNotification(error));
      return rejectWithValue(error);
    }
  },
);
